import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageHeader from '../../../components/PageHeader/PageHeader';
import CoreMaintenanceService from '../../../services/CoreMaintenanceService';
import CompanyService from '../../../services/CompanyService';

const POLL_INTERVAL_MS = 3000;

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusColor = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'RUNNING':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    case 'COMPLETED_WITH_ERRORS':
      return 'warning';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
};

const CoreMaintenancePage = () => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [startingCompany, setStartingCompany] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const data = await CoreMaintenanceService.getGlobalCostRecalculationStatus();
      setJob(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error_message || 'No se pudo obtener el estado del mantenimiento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const loadCompanies = async () => {
      const data = await CompanyService.getCompanies(1, 100000, '');
      const activeCompanies = data.filter((company) => company.isActive !== false);
      setCompanies(activeCompanies);
      setSelectedCompanyId((current) => current || activeCompanies[0]?.id || '');
    };

    loadCompanies();
  }, []);

  useEffect(() => {
    if (!job || job.status !== 'RUNNING') return undefined;

    const intervalId = window.setInterval(() => {
      loadStatus();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [job, loadStatus]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const data = await CoreMaintenanceService.startGlobalCostRecalculation();
      setJob(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error_message || 'No se pudo iniciar el recálculo global.');
    } finally {
      setStarting(false);
    }
  };

  const handleStartCompany = async () => {
    if (!selectedCompanyId) {
      setError('Seleccioná una empresa para recalcular.');
      return;
    }

    setStartingCompany(true);
    try {
      const data = await CoreMaintenanceService.startCompanyCostRecalculation(selectedCompanyId);
      setJob(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error_message || 'No se pudo iniciar el recálculo de la empresa.');
    } finally {
      setStartingCompany(false);
    }
  };

  const progressValue = useMemo(() => {
    if (!job) return 0;
    return Math.max(0, Math.min(100, Number(job.progressPercentage || 0)));
  }, [job]);

  const isRunning = job?.status === 'RUNNING';

  return (
    <Box p={3}>
      <PageHeader
        title="Mantenimiento"
        subtitle="Herramientas internas para recálculo masivo y tareas de soporte"
      />

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Recalcular costos globalmente"
          subheader="Ejecuta el algoritmo de costos para todas las empresas y locations activas."
        />
        <CardContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              Para evitar picos de CPU y memoria, priorizá recalcular una empresa específica. El proceso corre en background y se divide en etapas por location.
            </Alert>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                select
                size="small"
                label="Empresa"
                value={selectedCompanyId}
                onChange={(event) => setSelectedCompanyId(event.target.value)}
                disabled={isRunning || companies.length === 0}
                sx={{ minWidth: { xs: '100%', md: 280 } }}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
              <LoadingButton
                variant="contained"
                color="primary"
                loading={startingCompany}
                disabled={isRunning || !selectedCompanyId}
                onClick={handleStartCompany}
              >
                Recalcular empresa
              </LoadingButton>
              <LoadingButton
                variant="contained"
                color="warning"
                loading={starting}
                disabled={isRunning}
                onClick={handleStart}
              >
                Forzar recálculo global
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                loading={loading && !job}
                onClick={loadStatus}
              >
                Actualizar estado
              </LoadingButton>
              <Chip
                label={job?.status || 'IDLE'}
                color={statusColor(job?.status)}
                variant="outlined"
              />
            </Stack>

            <Box>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Progreso general</Typography>
                <Typography variant="body2">{progressValue.toFixed(1)}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={progressValue} />
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Typography variant="body2">
                <strong>Solicitado por:</strong> {job?.requestedBy || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Alcance:</strong> {job?.scope === 'COMPANY' ? 'Empresa' : 'Global'}
              </Typography>
              <Typography variant="body2">
                <strong>Inicio:</strong> {formatTimestamp(job?.startedAt)}
              </Typography>
              <Typography variant="body2">
                <strong>Fin:</strong> {formatTimestamp(job?.finishedAt)}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Typography variant="body2">
                <strong>Empresas:</strong> {job?.processedCompanies || 0} / {job?.totalCompanies || 0}
              </Typography>
              <Typography variant="body2">
                <strong>Empresa actual:</strong> {job?.currentCompanyName || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Location actual:</strong> {job?.currentLocationName || '-'}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardHeader title="Resumen por empresa" subheader="Resultado acumulado del último job global." />
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Locations</TableCell>
                <TableCell align="right">Insumos</TableCell>
                <TableCell align="right">Productos</TableCell>
                <TableCell align="right">Plantas</TableCell>
                <TableCell>Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(job?.summaries || []).map((summary) => (
                <TableRow key={`${summary.companyId}-${summary.companyName}`}>
                  <TableCell>{summary.companyName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={summary.success ? 'OK' : 'ERROR'}
                      color={summary.success ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {summary.locationsProcessed} / {summary.totalLocations}
                  </TableCell>
                  <TableCell align="right">{summary.supplyLocationsUpdated || 0}</TableCell>
                  <TableCell align="right">{summary.productLocationsUpdated || 0}</TableCell>
                  <TableCell align="right">{summary.plantsRecalculated || 0}</TableCell>
                  <TableCell>{summary.message || '-'}</TableCell>
                </TableRow>
              ))}
              {(!job?.summaries || job.summaries.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Todavía no hay ejecuciones registradas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CoreMaintenancePage;
